import { useVirtualListScrollRestore } from '@/hooks/use-virtual-list-scroll-restore';
import { useUnit } from 'effector-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { LogoLoader } from '@/components/logo-loader';
import {
	$addPushes,
	$cursorPushes,
	$hasMorePushes,
	$isFetchingPushes,
	$pushes,
	$setCursorPushes,
	$setHasMorePushes,
	$setIsFetchingPushes,
	$setPushes,
	type IPush,
} from '@/entities/push.entity';
import {
	$clientsFilter,
	$clientsSearchText,
	$clientsSortDir,
	$pushesFetchKey,
	$pushesListScrollTop,
	$pushesSearchField,
	$pushesSortBy,
	$setPushesFetchKey,
	$setPushesListScrollTop,
	onlineStatusToIsOnline,
	toServerPushesFilters,
} from '@/entities/clients-filter.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { mockGlobalPushes } from '@/mocks/fixtures';
import PushCard from '../cards/push-card';

const LIMIT = 35;
const SCROLL_COMPENSATION_THRESHOLD = 80;

export default function PushesList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const prevCountRef = useRef(0);
	const prevFirstIdRef = useRef<number | null>(null);

	const [pushes, addPushes, setPushes] = useUnit([$pushes, $addPushes, $setPushes]);
	const [hasMore, setHasMore] = useUnit([$hasMorePushes, $setHasMorePushes]);
	const [cursor, setCursor] = useUnit([$cursorPushes, $setCursorPushes]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingPushes, $setIsFetchingPushes]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$pushesSearchField,
		$pushesSortBy,
		$clientsSortDir,
		$pushesListScrollTop,
		$setPushesListScrollTop,
		$pushesFetchKey,
		$setPushesFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerPushesFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: pushes.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 150,
		overscan: 8,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useVirtualListScrollRestore({
		listType: 'pushes',
		listRef,
		virtualizer,
		items: pushes,
		savedScrollTop,
		setScrollTop,
		ready: firstFetched && pushes.length > 0,
	});

	useEffect(() => {
		if (isDevMode) {
			setPushes(mockGlobalPushes);
			setHasMore(false);
			return;
		}

		function addPushesHandler(data: { pushes: IPush[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);
			if (data?.pushes?.length) {
				addPushes(data.pushes);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on(SOCKET_EVENTS.addPushes, addPushesHandler);

		return () => {
			socket.off(SOCKET_EVENTS.addPushes, addPushesHandler);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setPushes([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit(SOCKET_EVENTS.getPushes, { ...queryPayload, cursor: null });
		}

		if (pushes.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);
			return () => clearTimeout(timeout);
		}
	}, [currentFetchKey]);

	useLayoutEffect(() => {
		const firstId = pushes[0]?.id ?? null;
		const isPrepend =
			pushes.length === prevCountRef.current + 1 && prevCountRef.current > 0 && firstId !== prevFirstIdRef.current;

		if (isPrepend && listRef.current) {
			const el = listRef.current.querySelector('[data-index="0"]') as HTMLElement | null;
			const height = el?.getBoundingClientRect().height ?? 150;

			if (listRef.current.scrollTop > SCROLL_COMPENSATION_THRESHOLD) {
				listRef.current.scrollTop += height + 8;
			}
		}

		prevCountRef.current = pushes.length;
		prevFirstIdRef.current = firstId;
	}, [pushes]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= pushes.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit(SOCKET_EVENTS.getPushes, { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, pushes.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !pushes.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LogoLoader />
				</div>
			) : pushes.length === 0 ? (
				<div className='text-center mt-30 text-md'>Push не найдены</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const push = pushes[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<PushCard push={push} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
